import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Coins, Trash, Copy } from "@phosphor-icons/react";

const ROLE_KEY = { owner: "collab.roleOwner", co_organizer: "collab.roleCo", registration: "collab.roleReg", observer: "collab.roleObs" };

function Num({ label, value, onChange, testid, min = 0 }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input type="number" min={min} value={value} onChange={(e) => onChange(parseInt(e.target.value) || 0)} data-testid={testid} />
    </div>
  );
}

function NumFloat({ label, value, onChange, testid, min = 0, max = 100, step = 0.1, hint }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input type="number" min={min} max={max} step={step} value={value}
        onChange={(e) => { const v = parseFloat(e.target.value); onChange(isNaN(v) ? min : Math.max(min, Math.min(max, v))); }} data-testid={testid} />
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function Collaborators({ eventId, myRole }) {
  const t = useT();
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("co_organizer");
  const [confirmTarget, setConfirmTarget] = useState(null);
  const isOwner = myRole === "owner";

  const load = React.useCallback(async () => {
    try { const r = await api.get(`/events/${eventId}/collaborators`); setData(r.data); } catch (e) { /* no access */ }
  }, [eventId]);
  React.useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!email) return;
    try {
      const { data: r } = await api.post(`/events/${eventId}/collaborators/invite`, { email, role });
      const url = `${window.location.origin}/invite/${r.token}`;
      setEmail("");
      await copyToClipboard(url);
      toast.success(t("collab.linkCreated"));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (em) => {
    try { await api.delete(`/events/${eventId}/collaborators/${encodeURIComponent(em)}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setConfirmTarget(null); }
  };

  return (
    <div className="border border-border rounded-lg p-5 bg-card" data-testid="collaborators-section">
      <h3 className="text-lg font-bold mb-1">{t("collab.title")}</h3>
      <p className="text-xs text-muted-foreground mb-4">{t("collab.desc")}</p>
      <div className="space-y-2">
        {data?.owner?.email && (
          <div className="flex items-center justify-between text-sm border border-border rounded-md px-3 py-2">
            <span className="truncate">{data.owner.name || data.owner.email}</span>
            <span className="text-[10px] uppercase tracking-widest font-bold text-brand-yellow">{t("collab.roleOwner")}</span>
          </div>
        )}
        {(data?.collaborators || []).map((c) => (
          <div key={c.email} className="flex items-center justify-between text-sm border border-border rounded-md px-3 py-2" data-testid={`collab-row-${c.email}`}>
            <span className="truncate">{c.name || c.email}</span>
            <div className="flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{t(ROLE_KEY[c.role] || "collab.roleObs")}</span>
              {isOwner && <button onClick={() => setConfirmTarget({ email: c.email, label: c.name || c.email })} className="text-muted-foreground hover:text-hong" data-testid={`collab-remove-${c.email}`}><Trash size={15} /></button>}
            </div>
          </div>
        ))}
        {(data?.pending || []).map((p) => {
          const url = `${window.location.origin}/invite/${p.token}`;
          return (
            <div key={p.token} className="border border-dashed border-border rounded-md px-3 py-2 space-y-2" data-testid={`collab-pending-${p.email}`}>
              <div className="flex items-center justify-between text-sm">
                <span className="truncate">{p.email}</span>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-gold">{t(ROLE_KEY[p.role] || "collab.roleObs")}</span>
                  {isOwner && <button onClick={() => setConfirmTarget({ email: p.email, label: p.email })} className="text-muted-foreground hover:text-hong" data-testid={`collab-remove-${p.email}`}><Trash size={15} /></button>}
                </div>
              </div>
              <button type="button" onClick={async () => { await copyToClipboard(url); toast.success(t("ring.copied")); }}
                className="w-full flex items-center justify-between gap-2 text-left font-monot text-xs bg-secondary border border-border rounded-md px-3 py-1.5 hover:border-gold" data-testid={`collab-pending-link-${p.email}`}>
                <span className="truncate">{url}</span><Copy size={14} className="shrink-0 text-muted-foreground" />
              </button>
            </div>
          );
        })}
        {data && (data.collaborators || []).length === 0 && (data.pending || []).length === 0 && <p className="text-sm text-muted-foreground">{t("collab.none")}</p>}
      </div>
      <AlertDialog open={!!confirmTarget} onOpenChange={(o) => { if (!o) setConfirmTarget(null); }}>
        <AlertDialogContent data-testid="collab-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("collab.confirmRemoveTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("collab.confirmRemoveDesc").replace("{name}", confirmTarget?.label || "")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="collab-remove-cancel">{t("dash.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove(confirmTarget.email)} className="bg-hong hover:bg-hong/90 text-white" data-testid="collab-remove-confirm">{t("collab.confirmRemoveBtn")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {isOwner && (
        <div className="mt-4 space-y-2">
          <Label>{t("collab.email")}</Label>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="mario@kihap.app" data-testid="collab-email-input" className="flex-1" />
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="sm:w-56" data-testid="collab-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="co_organizer">{t("collab.roleCo")}</SelectItem>
                <SelectItem value="registration">{t("collab.roleReg")}</SelectItem>
                <SelectItem value="observer">{t("collab.roleObs")}</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={invite} data-testid="collab-invite-btn">{t("collab.add")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SettingsTab({ eventId, event, onSaved }) {
  const t = useT();
  const nav = useNavigate();
  const [combat, setCombat] = useState(event.combat_settings);
  const [poomsae, setPoomsae] = useState(event.poomsae_settings);
  const [club, setClub] = useState(event.club_standings || { method: "medals", gold_points: 10, silver_points: 5, bronze_points: 3, double_bronze: false });
  const [ringPw, setRingPw] = useState(event.ring_password);
  const [saving, setSaving] = useState(false);
  const canEdit = ["owner", "co_organizer"].includes(event.my_role);

  const showCombat = event.type === "combat" || event.type === "mixed";
  const showPoomsae = event.type === "poomsae" || event.type === "mixed";

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/events/${eventId}`, {
        name: event.name, type: event.type, date: event.date,
        ring_password: ringPw, combat_settings: combat, poomsae_settings: poomsae, club_standings: club,
      });
      toast.success(t("set.saved"));
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">{t("set.title")}</h2>
      <p className="text-sm text-muted-foreground -mt-4">{t("set.sub")}</p>

      {showCombat && (
        <div className="border border-border rounded-lg p-5 bg-card">
          <h3 className="text-lg font-bold text-hong mb-4">{t("set.combat")}</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Num label={t("set.rounds")} value={combat.num_rounds} onChange={(v) => setCombat({ ...combat, num_rounds: v })} testid="cfg-num-rounds" min={1} />
            <Num label={t("set.roundDur")} value={combat.round_duration_sec} onChange={(v) => setCombat({ ...combat, round_duration_sec: v })} testid="cfg-round-duration" min={10} />
            <Num label={t("set.break")} value={combat.break_duration_sec} onChange={(v) => setCombat({ ...combat, break_duration_sec: v })} testid="cfg-break" />
            <Num label={t("set.koDiff")} value={combat.tech_ko_point_diff} onChange={(v) => setCombat({ ...combat, tech_ko_point_diff: v })} testid="cfg-ko-diff" />
            <Num label={t("set.judges")} value={combat.num_judges} onChange={(v) => setCombat({ ...combat, num_judges: v })} testid="cfg-combat-judges" min={1} />
            <NumFloat label={t("set.consensusWindow")} value={combat.consensus_window_sec ?? 0.4} onChange={(v) => setCombat({ ...combat, consensus_window_sec: v })} testid="cfg-consensus-window" min={0.2} max={2.0} step={0.1} hint={t("set.consensusHint")} />
          </div>
        </div>
      )}

      {showPoomsae && (
        <div className="border border-border rounded-lg p-5 bg-card">
          <h3 className="text-lg font-bold text-chung mb-4">{t("set.poomsae")}</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Num label={t("set.forms")} value={poomsae.num_forms} onChange={(v) => setPoomsae({ ...poomsae, num_forms: Math.max(1, v) })} testid="cfg-num-forms" min={1} />
            <Num label={t("set.judges")} value={poomsae.num_judges} onChange={(v) => setPoomsae({ ...poomsae, num_judges: v })} testid="cfg-poomsae-judges" min={1} />
          </div>
        </div>
      )}

      <div className="border border-border rounded-lg p-5 bg-card" data-testid="club-standings-card">
        <h3 className="text-lg font-bold text-gold mb-1">{t("club.title")}</h3>
        <p className="text-sm text-muted-foreground mb-4">{t("club.desc")}</p>
        <div className="space-y-1.5 max-w-sm">
          <Label>{t("club.method")}</Label>
          <Select value={club.method} onValueChange={(v) => setClub({ ...club, method: v })}>
            <SelectTrigger data-testid="club-method-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="medals">{t("club.methodMedals")}</SelectItem>
              <SelectItem value="points">{t("club.methodPoints")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {club.method === "points" && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 max-w-xl">
            <Num label={t("club.goldPts")} value={club.gold_points} onChange={(v) => setClub({ ...club, gold_points: v })} testid="club-gold-points" min={0} />
            <Num label={t("club.silverPts")} value={club.silver_points} onChange={(v) => setClub({ ...club, silver_points: v })} testid="club-silver-points" min={0} />
            <Num label={t("club.bronzePts")} value={club.bronze_points} onChange={(v) => setClub({ ...club, bronze_points: v })} testid="club-bronze-points" min={0} />
          </div>
        )}
        <div className="flex items-start justify-between gap-4 mt-5 pt-4 border-t border-border max-w-md">
          <div>
            <Label htmlFor="club-double-bronze">{t("club.doubleBronze")}</Label>
            <p className="text-xs text-muted-foreground mt-0.5">{t("club.doubleBronzeHint")}</p>
          </div>
          <Switch id="club-double-bronze" checked={!!club.double_bronze} onCheckedChange={(v) => setClub({ ...club, double_bronze: v })} data-testid="club-double-bronze-switch" />
        </div>
      </div>

      <div className="border border-border rounded-lg p-5 bg-card">
        <h3 className="text-lg font-bold mb-4">{t("set.ringAccess")}</h3>
        <div className="space-y-1.5 max-w-xs">
          <Label>{t("set.ringPw")}</Label>
          <Input value={ringPw} onChange={(e) => setRingPw(e.target.value)} data-testid="cfg-ring-password" />
          <p className="text-xs text-muted-foreground">{t("set.ringPwHint")}</p>
        </div>
      </div>

      <Collaborators eventId={eventId} myRole={event.my_role} />

      <div className="border border-border rounded-lg p-5 bg-card" data-testid="settings-tokens-info">
        <h3 className="text-lg font-bold mb-3 text-white">{t("set.tokensTitle")}</h3>
        <p className="text-sm text-muted-foreground mb-3">{t("set.tokensOwner")}</p>
        <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
          <li>{t("set.tokensPoomsae")}</li>
          <li>{t("set.tokensCombat")}</li>
        </ul>
        <p className="text-sm text-muted-foreground mt-3">{t("set.tokensCheck")}</p>
        <Button variant="outline" className="mt-4 gap-2" onClick={() => nav("/billing")} data-testid="settings-reload-tokens-btn">
          <Coins size={16} className="text-gold" /> {t("set.tokensReload")}
        </Button>
      </div>

      {canEdit && <Button onClick={save} disabled={saving} className="font-semibold" data-testid="save-settings-btn">{saving ? t("set.saving") : t("set.save")}</Button>}
    </div>
  );
}
